import { Controller } from "@hotwired/stimulus"

// controllers/range_picker_controller.js
//
// Drives the inline date-range picker in the sidebar filter panel. The
// calendar is rendered entirely client-side — month navigation, hover preview,
// and click-click selection never touch the server. Only *applying* a range
// (Apply / Enter / Clear / preset) submits the form, since that changes the SQL
// filter. The hidden range_start / range_end inputs are the source of truth on
// submit.
//
// State (client-only until the user commits):
//
//   draft     = { start: Date|null, end: Date|null }
//   hover     = Date | null
//   phase     = "start" | "end" | "complete"
//   viewMonth = Date (first of the displayed month)
//
// The form's hidden inputs are mutated only on a commit action; mid-flight
// edits stay in `draft`. The server contract is unchanged.
export default class extends Controller {
  static targets = [
    "card",
    "inputRow",
    "smartInput",
    "dayCount",
    "grid",
    "monthLabel",
    "customButton",
    "status",
    "applyButton",
    "clearButton",
    "hiddenStart",
    "hiddenEnd",
    "openInput",
  ]
  static values = {
    customOpen: { type: Boolean, default: false },
    start: { type: String, default: "" },
    end: { type: String, default: "" },
    today: { type: String, default: "" },
  }

  // ─── lifecycle ───────────────────────────────────────────────────────

  initialize() {
    this._onFormSubmit = this._onFormSubmit.bind(this)
  }

  connect() {
    this.today = this._iso(this.todayValue) || this._todayLocal()
    this.draft = {
      start: this._iso(this.startValue),
      end: this._iso(this.endValue),
    }
    this.hover = null
    this.phase =
      this.draft.start && this.draft.end
        ? "complete"
        : this.draft.start
          ? "end"
          : "start"
    this.viewMonth = this._startOfMonth(this.draft.start || this.today)
    this.form = this.element.closest("form")
    if (this.form) this.form.addEventListener("submit", this._onFormSubmit)
    this._render()
  }

  disconnect() {
    if (this.form) this.form.removeEventListener("submit", this._onFormSubmit)
  }

  // ─── open/close ───────────────────────────────────────────────────────

  toggleCustom(event) {
    event.preventDefault()
    this.customOpenValue = !this.customOpenValue
  }

  closeCustom() {
    this.customOpenValue = false
  }

  customOpenValueChanged() {
    if (this.hasCardTarget) {
      this.cardTarget.classList.toggle("hidden", !this.customOpenValue)
    }
    if (this.hasCustomButtonTarget) {
      this.customButtonTarget.setAttribute(
        "aria-expanded",
        this.customOpenValue ? "true" : "false",
      )
    }
    // Persist open state across filter submits (chip toggles, search) the same
    // way panel_open persists the panel — without this the calendar would snap
    // shut whenever another filter re-renders the sidebar.
    if (this.hasOpenInputTarget) {
      this.openInputTarget.value = this.customOpenValue ? "1" : "0"
    }
  }

  // ─── month navigation (client-side, no submit) ────────────────────────

  prevMonth(event) {
    if (event) event.preventDefault()
    this.viewMonth = this._addMonths(this.viewMonth, -1)
    this._renderCalendar()
  }

  nextMonth(event) {
    if (event) event.preventDefault()
    this.viewMonth = this._addMonths(this.viewMonth, 1)
    this._renderCalendar()
  }

  // ─── calendar interactions (event-delegated on the grid) ──────────────

  pickDay(event) {
    const cell = event.target.closest("[data-date]")
    if (!cell) return
    const day = this._iso(cell.dataset.date)
    if (!day) return
    if (this.phase === "start" || this.phase === "complete") {
      this.draft = { start: day, end: null }
      this.phase = "end"
    } else {
      const start = this.draft.start
      if (day < start) this.draft = { start: day, end: start }
      else this.draft = { start, end: day }
      this.phase = "complete"
    }
    this.hover = null
    this._renderInputValue()
    this._paintDays()
    this._renderStatus()
    this._renderActions()
    this._renderDayCount()
  }

  hoverDay(event) {
    if (this.phase !== "end") return
    const cell = event.target.closest("[data-date]")
    if (!cell) return
    const day = this._iso(cell.dataset.date)
    if (!day || (this.hover && this._key(day) === this._key(this.hover))) return
    this.hover = day
    this._paintDays()
  }

  clearHover() {
    if (!this.hover) return
    this.hover = null
    this._paintDays()
  }

  // ─── smart input ─────────────────────────────────────────────────────

  parse() {
    const text = this.smartInputTarget.value
    const result = parseNL(text, this.today)
    this._setInputState(text === "" ? "empty" : result ? "valid" : "invalid")
    if (result && result.start && result.end) {
      this.draft = { start: result.start, end: result.end }
      this.phase = "complete"
      this.hover = null
      this.viewMonth = this._startOfMonth(result.start)
      this._renderCalendar()
      this._renderStatus()
      this._renderActions()
      this._renderDayCount()
    }
  }

  commitInput(event) {
    event.preventDefault()
    if (this.phase !== "complete") return
    this._submit("custom")
  }

  // ─── render ──────────────────────────────────────────────────────────

  _render() {
    this._renderInputValue()
    this._renderCalendar()
    this._renderStatus()
    this._renderActions()
    this._renderDayCount()
  }

  _renderInputValue() {
    const { start, end } = this.draft
    if (start && end) {
      this.smartInputTarget.value = `${this._fmtShort(start)} – ${this._fmtShort(end)}`
      this._setInputState("valid")
    } else {
      this.smartInputTarget.value = ""
      this._setInputState("empty")
    }
  }

  // Rebuild the month grid for viewMonth (Mon-first, blanks for adjacent-month
  // days), then paint the current selection / hover state onto it.
  _renderCalendar() {
    if (this.hasMonthLabelTarget) {
      this.monthLabelTarget.textContent = this.viewMonth.toLocaleString(
        "en-US",
        { month: "long", year: "numeric" },
      )
    }
    if (!this.hasGridTarget) return

    const first = this.viewMonth
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0)
    const startOffset = (first.getDay() + 6) % 7 // days back to Monday
    const gridStart = this._addDays(first, -startOffset)
    const endOffset = 6 - ((last.getDay() + 6) % 7) // days fwd to Sunday
    const gridEnd = this._addDays(last, endOffset)
    const month = first.getMonth()
    const todayKey = this._key(this.today)

    const cells = []
    for (let d = gridStart; d <= gridEnd; d = this._addDays(d, 1)) {
      if (d.getMonth() !== month) {
        cells.push('<div class="h-6"></div>')
        continue
      }
      const dot =
        this._key(d) === todayKey
          ? '<span data-today-dot class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-vivid-purple"></span>'
          : ""
      cells.push(
        `<button type="button" data-date="${this._isoString(d)}" class="${this._dayClasses({})}">` +
          `<span class="relative">${d.getDate()}${dot}</span>` +
          `</button>`,
      )
    }
    this.gridTarget.innerHTML = cells.join("")
    this._paintDays()
  }

  // Apply selection / hover-preview styling to the rendered day buttons.
  _paintDays() {
    if (!this.hasGridTarget) return
    const { start, end } = this.draft
    let prevStart = start,
      prevEnd = end
    if (start && !end && this.hover) {
      if (this.hover < start) {
        prevStart = this.hover
        prevEnd = start
      } else {
        prevEnd = this.hover
      }
    }

    const startKey = prevStart && this._key(prevStart)
    const endKey = prevEnd && this._key(prevEnd)

    this.gridTarget.querySelectorAll("button[data-date]").forEach((btn) => {
      const d = this._iso(btn.dataset.date)
      if (!d) return
      const isStart = startKey && this._key(d) === startKey
      const isEnd = endKey && this._key(d) === endKey
      const inRange = prevStart && prevEnd && d > prevStart && d < prevEnd
      const isHoverPreview =
        !end && this.hover && this._key(d) === this._key(this.hover)

      btn.className = this._dayClasses({
        isStart,
        isEnd,
        inRange,
        isHoverPreview,
      })

      const dot = btn.querySelector("[data-today-dot]")
      if (dot) dot.classList.toggle("hidden", Boolean(isStart || isEnd))
    })
  }

  _renderStatus() {
    const t = this.statusTarget
    const { start, end } = this.draft
    if (this.phase === "start") {
      t.className = "text-[10px] text-app-text-faint italic"
      t.textContent = "Pick a start date"
    } else if (this.phase === "end" || !end) {
      t.className = "text-[10px] text-app-text-faint"
      t.textContent = "Pick an end date…"
    } else {
      const days = Math.round((end - start) / 86400000) + 1
      t.className = "text-[10px] text-app-text-secondary font-mono tabular-nums"
      t.innerHTML = `${this._fmtShort(start)} <span class="text-app-text-faint">→</span> ${this._fmtShort(end)}<span class="text-app-text-faint"> · ${days}d</span>`
    }
  }

  _renderActions() {
    const complete = this.phase === "complete"
    this.applyButtonTarget.disabled = !complete
    this.clearButtonTarget.disabled =
      this.phase === "start" && !this.startValue && !this.endValue
  }

  _renderDayCount() {
    if (!this.hasDayCountTarget) return
    const { start, end } = this.draft
    if (start && end) {
      const days = Math.round((end - start) / 86400000) + 1
      this.dayCountTarget.textContent = `${days}d`
      this.dayCountTarget.classList.remove("hidden")
    } else {
      this.dayCountTarget.classList.add("hidden")
    }
  }

  // ─── submit helpers ──────────────────────────────────────────────────

  _submit(mode) {
    this._syncHiddens()
    if (!this.form) return
    const modeInput = document.createElement("input")
    modeInput.type = "hidden"
    modeInput.name = "range"
    modeInput.value = mode
    this.form.appendChild(modeInput)
    this.form.requestSubmit()
  }

  _syncHiddens() {
    const { start, end } = this.draft
    this.hiddenStartTarget.value = start ? this._isoString(start) : ""
    this.hiddenEndTarget.value = end ? this._isoString(end) : ""
  }

  _onFormSubmit() {
    this._syncHiddens()
  }

  // ─── helpers ─────────────────────────────────────────────────────────

  _setInputState(kind) {
    const row = this.inputRowTarget
    row.classList.remove(
      "ring-app-border-subtle",
      "ring-vivid-purple/40",
      "ring-red-500/40",
    )
    if (kind === "valid") row.classList.add("ring-vivid-purple/40")
    else if (kind === "invalid") row.classList.add("ring-red-500/40")
    else row.classList.add("ring-app-border-subtle")
  }

  _dayClasses({ isStart, isEnd, inRange, isHoverPreview }) {
    const base =
      "h-6 text-[10.5px] flex items-center justify-center font-mono tabular-nums cursor-pointer transition-colors relative"
    if (isStart && isEnd)
      return `${base} bg-vivid-purple text-white rounded-full font-medium`
    if (isStart)
      return `${base} bg-vivid-purple text-white rounded-l-full font-medium`
    if (isEnd)
      return `${base} bg-vivid-purple text-white rounded-r-full font-medium`
    if (inRange) return `${base} bg-vivid-purple/20 text-app-text`
    if (isHoverPreview)
      return `${base} bg-vivid-purple/30 text-white rounded-r-full`
    return `${base} text-app-text-secondary hover:bg-app-surface-hover hover:text-app-text`
  }

  _iso(s) {
    if (!s) return null
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!m) return null
    return new Date(
      parseInt(m[1], 10),
      parseInt(m[2], 10) - 1,
      parseInt(m[3], 10),
    )
  }

  _isoString(d) {
    const yy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const dd = String(d.getDate()).padStart(2, "0")
    return `${yy}-${mm}-${dd}`
  }

  _key(d) {
    return this._isoString(d)
  }
  _fmtShort(d) {
    return d.toLocaleString("en-US", { month: "short", day: "numeric" })
  }

  _startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1)
  }
  _addMonths(d, n) {
    return new Date(d.getFullYear(), d.getMonth() + n, 1)
  }
  _addDays(d, n) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
  }
  _todayLocal() {
    const n = new Date()
    return new Date(n.getFullYear(), n.getMonth(), n.getDate())
  }
}

// ─── Natural-language parser ─────────────────────────────────────────────
const MONTH_NAMES = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
]

function parseNL(input, today) {
  const s = input.trim().toLowerCase()
  if (!s) return null
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  if (s === "today") return { start: t, end: t }
  if (s === "yesterday") {
    const y = addDays(t, -1)
    return { start: y, end: y }
  }
  if (s === "this week") {
    const dow = (t.getDay() + 6) % 7
    return { start: addDays(t, -dow), end: t }
  }
  if (s === "last week") {
    const dow = (t.getDay() + 6) % 7
    const monThis = addDays(t, -dow)
    return { start: addDays(monThis, -7), end: addDays(monThis, -1) }
  }
  if (s === "this month")
    return { start: new Date(t.getFullYear(), t.getMonth(), 1), end: t }
  if (s === "last month") {
    const startLast = new Date(t.getFullYear(), t.getMonth() - 1, 1)
    const endLast = new Date(t.getFullYear(), t.getMonth(), 0)
    return { start: startLast, end: endLast }
  }
  let m = s.match(/^last\s+(\d+)\s*(d|days?)$/)
  if (m) {
    const n = parseInt(m[1], 10)
    if (n >= 1 && n <= 365) return { start: addDays(t, -(n - 1)), end: t }
  }
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (m) {
    const d = mkISO(m[1], m[2], m[3])
    return d ? { start: d, end: d } : null
  }
  m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})\s*(?:-|–|—|to)\s*(\d{4})-(\d{2})-(\d{2})$/,
  )
  if (m) {
    const a = mkISO(m[1], m[2], m[3])
    const b = mkISO(m[4], m[5], m[6])
    if (a && b) return a <= b ? { start: a, end: b } : { start: b, end: a }
  }
  m = s.match(/^([a-z]{3,9})\s+(\d{1,2})(?:,\s*(\d{4}))?$/)
  if (m) {
    const d = mkMonthDay(m[1], m[2], m[3], t)
    return d ? { start: d, end: d } : null
  }
  m = s.match(
    /^([a-z]{3,9})\s+(\d{1,2})(?:,\s*(\d{4}))?\s*(?:-|–|—|to)\s*([a-z]{3,9})\s+(\d{1,2})(?:,\s*(\d{4}))?$/,
  )
  if (m) {
    const a = mkMonthDay(m[1], m[2], m[3], t)
    const b = mkMonthDay(m[4], m[5], m[6], t)
    if (a && b) return a <= b ? { start: a, end: b } : { start: b, end: a }
  }
  return null
}

function mkISO(y, mo, d) {
  const yy = parseInt(y, 10),
    mm = parseInt(mo, 10),
    dd = parseInt(d, 10)
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null
  return new Date(yy, mm - 1, dd)
}

function mkMonthDay(monStr, day, year, today) {
  const idx = MONTH_NAMES.findIndex((n) => n.startsWith(monStr.slice(0, 3)))
  if (idx < 0) return null
  let yy = year ? parseInt(year, 10) : today.getFullYear()
  const dd = parseInt(day, 10)
  if (dd < 1 || dd > 31) return null
  let result = new Date(yy, idx, dd)
  if (!year && result - today > 7 * 86400000) {
    yy -= 1
    result = new Date(yy, idx, dd)
  }
  return result
}

function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
}
